/**
 * bulkSettingsError — turns a failed group bulk-settings request (item 226)
 * into one of a few kinds the dialog can act on: a stale preview re-previews,
 * a refused setting stays on the settings step, a lost response is reported as
 * "not confirmed" rather than as a failure.
 */

import { isAxiosError } from 'axios'

export type BulkSettingsErrorKind = 'stale' | 'invalid' | 'forbidden' | 'not_found' | 'throttled' | 'network' | 'server' | 'aborted'

export interface BulkSettingsError { kind: BulkSettingsErrorKind; message: string; retryAfterSeconds: number | null }

function flatten(data: unknown): string {
  if (!data || typeof data !== 'object') return ''
  const d = data as Record<string, unknown>
  if (Array.isArray(d.unknown_camera_ids)) {
    return `${String(d.detail ?? 'Unknown cameras')} (${(d.unknown_camera_ids as unknown[]).join(', ')})`
  }
  if (typeof d.detail === 'string') return d.detail
  return Object.entries(d).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(' ') : String(v)}`).join('; ')
}

export function classifyBulkSettingsError(err: unknown): BulkSettingsError {
  const base = { retryAfterSeconds: null as number | null }
  if (!isAxiosError(err)) return { ...base, kind: 'server', message: 'Something went wrong.' }
  if (err.code === 'ERR_CANCELED') return { ...base, kind: 'aborted', message: '' }
  const res = err.response
  if (!res) {
    return { ...base, kind: 'network',
      message: 'We could not confirm whether this was applied. Refresh the preview to check.' }
  }
  const msg = flatten(res.data)
  switch (res.status) {
    case 409: return { ...base, kind: 'stale', message: msg || 'The preview is out of date.' }
    case 400: return { ...base, kind: 'invalid', message: msg || 'The server refused these settings.' }
    case 403: return { ...base, kind: 'forbidden', message: 'Only superusers can apply group settings.' }
    case 404: return { ...base, kind: 'not_found', message: 'This group no longer exists.' }
    case 429: {
      const raw = Number((res.headers as Record<string, string>)['retry-after'])
      const secs = Number.isFinite(raw) && raw > 0 ? raw : null
      return { kind: 'throttled', retryAfterSeconds: secs,
        message: secs ? `Too many requests. Try again in ${Math.ceil(secs / 60)} min.` : 'Too many requests. Try again later.' }
    }
    default: return { ...base, kind: 'server', message: 'The server failed. Nothing is known to have changed; refresh the preview to check.' }
  }
}
